import { useSelector } from 'react-redux'
import props from '../../../../../../redux/props'
import useConceptConfiguration from '../../ConceptCalculation/useConceptConfiguration'
import { Floor } from '../../../../../../types/Survey/Survey'
import { Concept, FloorMappingEntryType } from '../../../../../../types/Survey/Analysis/Concept'
import Item from '../Item'
import BarStacked, { Item as BarStackedItem } from '../../../../../UI/Chart/BarStacked/BarStacked'
import { ProductionDistance } from '../../../../../../types/OrganizationUnit'
import { isUnitAllowedByProductionDistance } from '../SummaryProductionDistance'
import Button from '../../../../../UI/Button/Button'
import Modal from '../../../../../UI/Modal/Modal'
import { useState } from 'react'
import FloorQuickAssignment from './FloorQuickAssignment'
import FloorComparison from './FloorComparison'
import TableItem, { TableItemRow } from '../TableItem'

export type FloorListItemUnit = {
  type: FloorMappingEntryType
  uuid: string
  name: string
  totalSquareMeter: number
}

export type FloorListItem = {
  floor: Floor
  items: FloorListItemUnit[]
}

type Props = {
  concept: Concept
  productionDistance?: ProductionDistance
}

const FloorAnalysis: React.FC<Props> = ({ concept, productionDistance }) => {
  const { context } = useConceptConfiguration()
  const t = useSelector((s) => s[props.TRANSLATION])
  const [show, setShow] = useState(false)
  const toggle = () => setShow(!show)

  const getUnitName = (unitUUID: string) => {
    if (!!concept.units[unitUUID]) {
      return concept.units[unitUUID].name
    }
    return ''
  }

  const getUebergeordneterBedarfName = (bedarfUUID: string) => {
    const bedarf = concept.optimization?.UEBERGEORDNETE_BEDARFE.find((bedarf) => bedarf.uuid === bedarfUUID)
    if (bedarf) {
      return bedarf.name
    }
    return ''
  }

  const getUnitTotalSquareMeter = (unitUUID: string) => {
    if (!!concept.units[unitUUID]) {
      return concept.units[unitUUID].ergebnisse.total
    }
    return 0
  }

  const getUebergeordneterBedarfTotalSquareMeter = (bedarfUUID: string) => {
    const bedarf = concept.optimization?.UEBERGEORDNETE_BEDARFE.find((bedarf) => bedarf.uuid === bedarfUUID)
    if (bedarf) {
      return bedarf.practicalQM
    }
    return 0
  }

  const getRequiredSquareMeter = (floorListItem: FloorListItem) => {
    if (!Array.isArray(concept.floorMapping)) return 0
    return concept.floorMapping.reduce((acc, mapping) => {
      if (mapping.floorUUID === floorListItem.floor.uuid) {
        if (mapping.type === FloorMappingEntryType.UNIT) {
          return acc + getUnitTotalSquareMeter(mapping.targetUUID)
        } else if (mapping.type === FloorMappingEntryType.UEBERGEORDNETER_BEDARF) {
          return acc + getUebergeordneterBedarfTotalSquareMeter(mapping.targetUUID)
        }
      }
      return acc
    }, 0)
  }

  const getFloorList = (): FloorListItem[] => {
    const floors: FloorListItem[] = []
    if (!Array.isArray(context.floors)) return []
    for (const floor of context.floors) {
      if (!concept.floorMapping) {
        floors.push({
          floor,
          items: [],
        })
      } else {
        const items = concept.floorMapping.reduce((acc, mapping) => {
          if (mapping.floorUUID === floor.uuid) {
            if (mapping.type === FloorMappingEntryType.UNIT) {
              acc.push({
                type: mapping.type,
                uuid: mapping.targetUUID,
                name: getUnitName(mapping.targetUUID),
                totalSquareMeter: getUnitTotalSquareMeter(mapping.targetUUID),
              })
            }
            if (mapping.type === FloorMappingEntryType.UEBERGEORDNETER_BEDARF) {
              acc.push({
                type: mapping.type,
                uuid: mapping.targetUUID,
                name: getUebergeordneterBedarfName(mapping.targetUUID),
                totalSquareMeter: getUebergeordneterBedarfTotalSquareMeter(mapping.targetUUID),
              })
            }
          }
          return acc
        }, [] as FloorListItemUnit[])
        floors.push({
          floor,
          items,
        })
      }
    }
    if (productionDistance) {
      return floors.filter(
        (floor) =>
          !!floor.items.find(
            (item) => item.type === FloorMappingEntryType.UNIT && isUnitAllowedByProductionDistance(concept, item.uuid, productionDistance),
          ),
      )
    }
    return floors
  }

  const getTotalAvailableSquareMeter = () => {
    if (productionDistance) {
      return getFloorList().reduce((acc, floor) => {
        return acc + floor.floor.squareMeter
      }, 0)
    }
    return context.floors?.reduce((acc, floor) => acc + floor.squareMeter, 0) || 0
  }

  const getTotalRequiredSquareMeter = () => {
    return getFloorList().reduce((acc, floor) => acc + getRequiredSquareMeter(floor), 0)
  }

  const getItems = (floor: FloorListItem): BarStackedItem[] => {
    return [
      {
        key: floor.floor.uuid,
        label: floor.floor.name,
        limit: floor.floor.squareMeter,
        values: floor.items.map((item) => ({
          label: item.name,
          value: item.totalSquareMeter,
        })),
      },
    ]
  }

  if (!context) return null
  return (
    <div>
      <div className="border-b border-blue font-bold">{t.survey.analysis.concept.summary.floors.headline}</div>
      {!productionDistance && (
        <div className="flex gap-3 my-3">
          <FloorQuickAssignment concept={concept} />
          {(!productionDistance || (!!productionDistance && getFloorList().length > 0)) && (
            <Button small text={t.survey.analysis.concept.summary.floors.showDetail} onClick={toggle} />
          )}
        </div>
      )}
      {getFloorList().map((item) => (
        <TableItem
          title={item.floor.name}
          value={getRequiredSquareMeter(item)}
          suffix={`${t.generic.squareMeterSuffix} von ${item.floor.squareMeter}${t.generic.squareMeterSuffix}`}
          key={item.floor.uuid}
          items={item.items.map((item) => (
            <TableItemRow
              key={item.uuid}
              title={item.name}
              values={[
                {
                  value: item.totalSquareMeter,
                  suffix: t.generic.squareMeterSuffix,
                },
              ]}
            />
          ))}
        >
          <BarStacked
            useTransparentColors={false}
            items={getItems(item)}
            suffix={t.generic.squareMeterSuffix}
            showGrid={true}
            enableDownload
            percentageWidth={0.3}
            showLegend
          />
        </TableItem>
      ))}
      <Item
        bold
        title={t.survey.analysis.concept.summary.floors.total}
        value={getTotalRequiredSquareMeter()}
        suffix={`${t.generic.squareMeterSuffix} von ${getTotalAvailableSquareMeter()}${t.generic.squareMeterSuffix}`}
        doRound
      />
      <Modal large noForm show={show} onClose={toggle} title={t.survey.analysis.concept.summary.floors.headline}>
        <FloorComparison concept={concept} productionDistance={productionDistance} />
      </Modal>
    </div>
  )
}

export default FloorAnalysis
